import { useState, useEffect, useContext } from "react";
import { DashboardLayout } from "@/components/layout/dashboard-layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Loader2,
  Package,
  CheckCircle,
  XCircle,
  Upload
} from "lucide-react";
import { Product, ProductFormData, SelectData } from "@/types/api";
import { productService, categoryService } from "@/services/api";
import { useToast } from "@/hooks/use-toast";

const ProductsPage = () => {
const { toast } = useToast();
  const [products, setProducts] = useState<Product[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<SelectData[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingCategories, setIsLoadingCategories] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState<ProductFormData>({
    name: "",
    description: "",
    model: "",
    categoryId: 0,
    active: true
  });
  const [isEditing, setIsEditing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  // Fetch products and categories on component mount
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setIsLoadingCategories(true);

      try {
        const [productsData, categoriesData] = await Promise.all([
          await productService.getItems(),
          await categoryService.getItems()
        ]);

        setProducts(productsData);
        setFilteredProducts(productsData);
        setCategories(categoriesData);
      } catch (error) { 
        toast({
          variant: "destructive",
          title: "Fetch Categories/Products",
          description: error instanceof Error ? error.message : error
        });
      } finally {
        setIsLoading(false);
        setIsLoadingCategories(false);
      }
    };

    fetchData();
  }, []);

  // Filter products based on search query
  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredProducts(products);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = products.filter(product =>
        product.name.toLowerCase().includes(query) ||
        (product.description && product.description.toLowerCase().includes(query)) ||
        (product.model && product.model.toLowerCase().includes(query)) ||
        product.categoryName.toLowerCase().includes(query)
      );
      setFilteredProducts(filtered);
    }
  }, [searchQuery, products]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSwitchChange = (name: string, checked: boolean) => {
    setFormData({
      ...formData,
      [name]: checked
    });
  };

  const handleCategoryChange = (categoryId: number) => {
    setFormData({
      ...formData,
      categoryId
    });
  };

  const handleAddProduct = () => {
    setIsEditing(false);
    setFormData({
      name: "",
      description: "",
      model: "",
      categoryId: 0,
      active: true
    });
    setIsDialogOpen(true);
  };

  const handleEditProduct = (product: Product) => {
    setIsEditing(true);
    setFormData({
      id: product.id,
      name: product.name,
      description: product.description,
      model: product.model,
      categoryId: product.categoryId,
      active: product.active
    });
    setIsDialogOpen(true);
  };

  const handleDeleteProduct = async (productId: number) => {
    if (window.confirm("Are you sure you want to delete this product?")) {
      const affectedRows: number = await productService.deleteItem(productId);
      if (affectedRows > 0) {
        toast({
          title: "Delete Product",
          description: "Deleted successfully",
        });
        setProducts(products.filter(product => product.id !== productId));
      }
      else 
      toast({
        variant: "destructive",
        title: "Delete Product",
        description: "Unable to delete",
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.categoryId) {
      toast({
        variant: "destructive",
        title: "Save Product",
        description: "Please select a category"
      });
      return;
    }

    const selectedCategory = categories.find(c => c.id === formData.categoryId);
    if (!selectedCategory) return;

    if (isEditing) {
      await productService.updateItem(formData);
      toast({
        title: "Update Product",
        description: "Updated successfully",
      });
      setProducts(products.map(product =>
        product.id === formData.id
          ? {
            ...formData as Product,
            categoryName: selectedCategory.name
          }
          : product
      ));
    } else {
      const newProduct: Product = {
        id: 0,
        name: formData.name,
        description: formData.description,
        model: formData.model,
        categoryId: formData.categoryId,
        categoryName: selectedCategory.name,
        active: formData.active
      };
      newProduct.id = await productService.createItem(newProduct);
      if (newProduct.id > 0) {
        toast({
          title: "Add Product",
          description: "Added successfully",
        });
        setProducts([...products, newProduct]);
      }
    }
    setIsDialogOpen(false);
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;
  
      // Check if the file is a CSV
      if (file.type !== "text/csv" && !file.name.endsWith('.csv')) {
        toast({
          variant: "destructive",
          title: "Invalid file format",
          description: "Please upload a CSV file.",
        });
        return;
      }
  
      setIsUploading(true);
  
      try {
        const formData = new FormData();
        formData.append('file', file);
  
        // Replace with your actual API endpoint
        const response = await productService.upload(formData);
        toast({
          title: "Upload Products",
          description: response,
        });
      } catch (error) {
        toast({
          variant: "destructive",
          title: "Upload Failed",
          description: error instanceof Error ? error.message : error, //"An error occurred while uploading the file.",
        });
      } finally {
        setIsUploading(false);
        event.target.value = ''; // Reset the file input
      }
    };
  
    const triggerFileInput = () => {
      document.getElementById('csv-upload')?.click();
    };
  
  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">Product Management</h1>
            <p className="text-muted-foreground">Manage inventory products</p>
          </div>
          <div className="flex space-x-2">
            <Button onClick={handleAddProduct}>
              <Plus size={16} className="mr-2" />
              Add Product
            </Button>
            <Button onClick={triggerFileInput} disabled={isUploading}>
              <Upload size={16} className="mr-2" />
              {isUploading ? "Uploading..." : "Upload CSV"}
            </Button>
            <input
              id="csv-upload"
              type="file"
              accept=".csv"
              className="hidden"
              onChange={handleFileUpload}
            />
          </div>
        </div>

        {/* Search bar */}
        <div className="relative w-full md:w-96">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Search products..."
            className="pl-8 w-full"
            value={searchQuery}
            onChange={handleSearchChange}
          />
        </div>

        {/* Products table */}
        <div className="border rounded-md">
          <Table>
            <TableHeader>
              <TableRow>
              <TableHead>Category</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Model</TableHead>
                <TableHead>Active</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-10">
                    <div className="flex justify-center">
                      <Loader2 size={30} className="animate-spin text-primary" />
                    </div>
                    <p className="mt-2 text-muted-foreground">Loading products...</p>
                  </TableCell>
                </TableRow>
              ) : filteredProducts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-10 text-muted-foreground">
                    {searchQuery ? "No products found matching your search" : "No products found"}
                  </TableCell>
                </TableRow>
              ) : (
                filteredProducts.map((product) => (
                  <TableRow key={product.id}>
                    <TableCell>{product.categoryName}</TableCell>
                    <TableCell className="font-medium">{product.name}</TableCell>
                    <TableCell className="max-w-md truncate">{product.description}</TableCell>
                    <TableCell>{product.model}</TableCell>
                    <TableCell>
                      {product.active ? (
                        <CheckCircle size={18} className="text-green-500" />
                      ) : (
                        <XCircle size={18} className="text-red-500" />
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEditProduct(product)}
                        >
                          <Edit size={16} className="text-blue-500" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteProduct(product.id)}
                        >
                          <Trash2 size={16} className="text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Add/Edit Product Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>{isEditing ? "Edit Product" : "Add New Product"}</DialogTitle>
              <DialogDescription>
                {isEditing
                  ? "Update product information"
                  : "Fill in the information to create a new product"}
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit}>
              <div className="grid gap-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Product Name</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="categoryId">Category</Label>
                  <Select
                    value={formData.categoryId.toString()}
                    onValueChange={(value) => handleCategoryChange(Number(value))}
                    disabled={isLoadingCategories}
                  >
                    <SelectTrigger id="categoryId">
                      <SelectValue placeholder={isLoadingCategories ? "Loading categories..." : "Select a category"} />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(category => (
                        <SelectItem key={category.id} value={category.id.toString()}>
                          {category.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="model">
                    Model <span className="text-muted-foreground">(Optional)</span>
                  </Label>
                  <Input
                    id="model"
                    name="model"
                    value={formData.model}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">
                    Description <span className="text-muted-foreground">(Optional)</span>
                  </Label>
                  <Textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={3}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <Label htmlFor="active">Active</Label>
                  <Switch
                    id="active"
                    checked={formData.active}
                    onCheckedChange={(checked) => handleSwitchChange('active', checked)}
                  />
                </div>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={!formData.categoryId}>
                  {isEditing ? "Update Product" : "Create Product"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default ProductsPage;